import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useClickOutside from "../../Hooks/useClickOutside";
import {
  AdminPageTitle,
  AdminMainText,
  AdminMetadataText,
  AdminSupportingText,
  AdminTableBody,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  adminFormInputClasses,
  adminWizardInputClasses,
} from "../AdminDashboard/shared/adminUx";
import { POPUP_FORM_CONTENT_WIDTH_CLASS } from "../../components/popup/popupLayoutSpec";
import { runTextPipeline, validateFile, getAcceptAttribute } from "../../utils/contentSafety";
import Alerts from "../../components/common/Alerts/Alerts";
import Popup from "../../components/popup/Popup";
import { inboxThreadHeightClass, businessChatMessageRowClass, businessChatBubbleClass, BUSINESS_CHAT_INCOMING_CARD_CLASS, BUSINESS_CHAT_AVATAR_CLASS } from "../BusinessDashboard/shared/businessLayoutSpec";

const MessageLayout = ({
  moveBack,
  customerprofileImage,
  customermessenger,
  customerconversationText,
  profileImage,
  businessProfileLink,
  messenger,
  sender,
  timing,
  conversationText,
  isQuote = false,
}) => {
  const { pathname } = useLocation();
  const threadHeightClass = inboxThreadHeightClass(pathname);
  const [message, setMessage] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportStep, setReportStep] = useState(1);
  const [reportReason, setReportReason] = useState("");
  const [reportBlock, setReportBlock] = useState(null);
  const [chatBlocked, setChatBlocked] = useState(false);
  const dotMenuRef = useRef(null);
  const fileInputRef = useRef(null);

  const REPORT_REASONS = [
    "Spam",
    "Harassment or bullying",
    "Inappropriate content",
    "Scam or fraud",
    "Impersonation",
    "Other",
  ];

  const reportOptionClass = (selected) =>
    [
      "w-full text-left px-4 py-3 rounded-lg border border-solid transition-colors font-inter",
      adminFormInputClasses(false, false),
      "!max-w-full",
      selected
        ? "!border-primaryblue bg-pastalblue text-offblack"
        : "text-offblack hover:border-primaryblue",
    ].join(" ");
  // Helper function to get display labels for quote form values
  const getJobTypeLabel = (value) => {
    const labels = {
      'repair': 'Repair',
      'replacement': 'Replacement',
      'new_installation': 'New Installation',
      'maintenance': 'Maintenance',
      'not_sure': 'Not Sure'
    };
    return labels[value] || value;
  };

  const getPropertyTypeLabel = (value) => {
    const labels = {
      'house': 'House',
      'apartment': 'Apartment',
      'townhouse': 'Townhouse',
      'unit': 'Unit',
      'duplex': 'Duplex',
      'commercial': 'Commercial',
      'other': 'Other'
    };
    return labels[value] || value;
  };

  const getSitePurposeLabel = (value) => {
    const labels = {
      'residential': 'Residential',
      'commercial': 'Commercial'
    };
    return labels[value] || value;
  };

  const getUrgencyLabel = (value) => {
    const labels = {
      'urgently': 'Urgent (1–2 days)',
      'this_week': 'This Week',
      'next_week': 'Next Week',
      'flexible': 'Flexible'
    };
    return labels[value] || value;
  };

  // Quote card data (dummy data for now)
  const quoteCardData = isQuote ? {
    id: -1,
    type: "quote",
    sender: "customer",
    timestamp: "1 week ago",
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    avatar: customerprofileImage,
    name: customermessenger,
    quoteData: {
      jobTitle: "Complete Bathroom Renovation",
      jobLocation: "Sydney, NSW 2000",
      jobDescription: "I need a complete bathroom renovation including tiling, plumbing, and fixtures. The space is approximately 3x2 meters. Looking for modern design with quality materials.",
      jobType: "new_installation",
      propertyType: "house",
      sitePurpose: "residential",
      urgency: "this_week",
      photos: [
        { url: "https://images.unsplash.com/photo-1620626011761-996317b8d101?w=400&h=400&fit=crop" },
        { url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=400&fit=crop" }
      ]
    }
  } : null;

  const [messages, setMessages] = useState([
    // Messages from 1 week ago
    {
      id: 0,
      text: "Hi! I have a leaky faucet in my kitchen. Could you help me with that?",
      sender: "customer",
      timestamp: "1 week ago",
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    },
    {
      id: 0.1,
      text: "Hello! I'd be happy to help you with that. Let me send you our pricing information for faucet repairs.",
      sender: "business",
      timestamp: "1 week ago",
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000), // 1 week ago + 30 minutes
      avatar: profileImage,
      name: messenger,
      type: "text"
    },
    // Messages from 5 days ago
    {
      id: 0.2,
      text: "Thanks for the information! I'll review it and get back to you.",
      sender: "customer",
      timestamp: "5 days ago",
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    },
    {
      id: 0.3,
      text: "Perfect! Take your time. Feel free to reach out if you have any questions about the repair.",
      sender: "business",
      timestamp: "5 days ago",
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000), // 5 days ago + 15 minutes
      avatar: profileImage,
      name: messenger,
      type: "text"
    },
    // Messages from 3 days ago
    {
      id: 0.4,
      text: "I've reviewed the pricing and I'm very interested. When can you come to fix the faucet?",
      sender: "customer",
      timestamp: "3 days ago",
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    },
    {
      id: 0.5,
      text: "Great! I'm available tomorrow afternoon. Does 2 PM work for you?",
      sender: "business",
      timestamp: "3 days ago",
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000 + 20 * 60 * 1000), // 3 days ago + 20 minutes
      avatar: profileImage,
      name: messenger,
      type: "text"
    },
    // Messages from 2 days ago
    {
      id: 0.6,
      text: "That works perfectly! I'll send you my address shortly.",
      sender: "customer",
      timestamp: "2 days ago",
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    },
    {
      id: 0.7,
      text: "Excellent! Looking forward to fixing your faucet.",
      sender: "business",
      timestamp: "2 days ago",
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 10 * 60 * 1000), // 2 days ago + 10 minutes
      avatar: profileImage,
      name: messenger,
      type: "text"
    },
    // Messages from yesterday
    {
      id: 0.8,
      text: "Hi! Just confirming our appointment for today at 2 PM. Still good?",
      sender: "customer",
      timestamp: "Yesterday",
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // Yesterday
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    },
    {
      id: 0.9,
      text: "Yes, absolutely! I’ll be there at 2 PM sharp to get your faucet fixed right away",
      sender: "business",
      timestamp: "Yesterday",
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000 + 5 * 60 * 1000), // Yesterday + 5 minutes
      avatar: profileImage,
      name: messenger,
      type: "text"
    },
    // Messages from today
    {
      id: 1,
      text: "Hi! I wanted to follow up on the faucet repair you did yesterday. It's working great now!",
      sender: "customer",
      timestamp: "2 hours ago",
      date: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    },
    {
      id: 2,
      text: "That's wonderful to hear! I'm glad the faucet is working properly now. Is there anything else you need help with?",
      sender: "business",
      timestamp: "1 hour ago",
      date: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      avatar: profileImage,
      name: messenger,
      type: "text"
    },
    {
      id: 3,
      text: "Thank you for the quick response! I really appreciate your help with the plumbing repair.",
      sender: "customer",
      timestamp: "30 minutes ago",
      date: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
      avatar: customerprofileImage,
      name: customermessenger,
      type: "text"
    }
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close menu when clicking outside
  useClickOutside(dotMenuRef, () => {
    setShowMenu(false);
  });

  const handleSendMessage = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;
    const result = runTextPipeline(trimmedMessage, "message");
    if (!result.valid) {
      Alerts.error(result.message);
      return;
    }
    const now = new Date();
    const newMessage = {
      id: messages.length + 1,
      text: result.normalized ?? trimmedMessage,
      sender: "business",
      timestamp: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: now,
      avatar: profileImage,
      name: messenger,
      type: "text"
    };
    setMessages([...messages, newMessage]);
    setMessage("");
  };

  const handleComposerKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file selection (for Attachment menu option)
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = (file.type || "").startsWith("image/");
    const result = validateFile(file, isImage ? "image" : "document");
    if (!result.valid) {
      Alerts.error(result.message);
      e.target.value = "";
      return;
    }
    const now = new Date();
    const newMessage = {
      id: messages.length + 1,
      text: file.name,
      sender: "business",
      timestamp: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: now,
      avatar: profileImage,
      name: messenger,
      type: "file",
      fileName: file.name,
      fileSize: (file.size / 1024 / 1024).toFixed(1) + " MB"
    };
    setMessages([...messages, newMessage]);
    e.target.value = "";
  };

  // Helper function to format date for display
  const formatDateForDisplay = (date) => {
    const messageDate = new Date(date);
    return messageDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to check if two dates are on the same day
  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const grouped = [];
    let currentDate = null;
    let quoteCardAdded = false;
    
    messages.forEach((message, index) => {
      // Add quote card as first item if it's a quote conversation
      if (isQuote && quoteCardData && !quoteCardAdded && index === 0) {
        if (!currentDate || !isSameDay(currentDate, quoteCardData.date)) {
          grouped.push({
            type: 'date-separator',
            date: quoteCardData.date,
            formattedDate: formatDateForDisplay(quoteCardData.date)
          });
          currentDate = quoteCardData.date;
        }
        grouped.push(quoteCardData);
        quoteCardAdded = true;
      }
      
      if (!currentDate || !isSameDay(currentDate, message.date)) {
        // Add date separator
        grouped.push({
          type: 'date-separator',
          date: message.date,
          formattedDate: formatDateForDisplay(message.date)
        });
        currentDate = message.date;
      }
      grouped.push(message);
    });
    
    return grouped;
  };

  return (
    <>
      {/* Chat Container */}
      <div className={`bg-white rounded-xl flex flex-col ${threadHeightClass}`}>
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 px-28 space-y-4 hidden-scrollbar" style={{
          scrollbarWidth: 'none'
        }}>
          {groupMessagesByDate(messages).map((item, index) => {
            // Render date separator
            if (item.type === 'date-separator') {
              return (
                <div key={`date-${index}`} className="flex items-center justify-center my-4">
                  <AdminMetadataText className="font-semibold text-offblack">
                    {item.formattedDate}
                  </AdminMetadataText>
                </div>
              );
            }

            // Render quote card
            if (item.type === "quote" && item.quoteData) {
              const quote = item.quoteData;
              // Parse photos if it's a string
              let parsedPhotos = [];
              if (quote.photos) {
                if (typeof quote.photos === 'string') {
                  try {
                    parsedPhotos = JSON.parse(quote.photos);
                  } catch {
                    parsedPhotos = [];
                  }
                } else if (Array.isArray(quote.photos)) {
                  parsedPhotos = quote.photos;
                }
              }
              
              return (
                <div key={item.id} className="flex justify-start">
                  <div className="flex flex-row items-start gap-2 max-w-[48%]">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className={BUSINESS_CHAT_AVATAR_CLASS}
                      />
                    </div>
                    
                    {/* Quote Card */}
                    <div className="flex flex-col">
                      <div className={BUSINESS_CHAT_INCOMING_CARD_CLASS}>
                        {/* Job Title */}
                        <div className="mb-3">
                          <AdminTableBody emphasis className="text-offblack text-base">
                            {quote.jobTitle}
                          </AdminTableBody>
                        </div>

                        <div className="mb-3 flex items-center">
                          <i className="fi fi-rs-marker mr-2 text-xs text-mediumgray" aria-hidden />
                          <AdminMainText className="text-offblack">{quote.jobLocation}</AdminMainText>
                        </div>

                        <div className="mb-3">
                          <AdminMainText className="text-offblack leading-relaxed">
                            {quote.jobDescription}
                          </AdminMainText>
                        </div>
                        
                        {/* Separator */}
                        <div className="border-t border-gray-300 my-3"></div>
                        
                        {/* Quote Details Grid */}
                        <div className="space-y-2 mb-3">
                          <div className="flex items-start gap-2">
                            <AdminTableBody emphasis className="text-offblack min-w-[100px] shrink-0">
                              Job Type:
                            </AdminTableBody>
                            <AdminMainText className="text-offblack">{getJobTypeLabel(quote.jobType)}</AdminMainText>
                          </div>
                          <div className="flex items-start gap-2">
                            <AdminTableBody emphasis className="text-offblack min-w-[100px] shrink-0">
                              Property:
                            </AdminTableBody>
                            <AdminMainText className="text-offblack">
                              {getPropertyTypeLabel(quote.propertyType)}
                            </AdminMainText>
                          </div>
                          <div className="flex items-start gap-2">
                            <AdminTableBody emphasis className="text-offblack min-w-[100px] shrink-0">
                              Category:
                            </AdminTableBody>
                            <AdminMainText className="text-offblack">
                              {getSitePurposeLabel(quote.sitePurpose)}
                            </AdminMainText>
                          </div>
                          <div className="flex items-start gap-2">
                            <AdminTableBody emphasis className="text-offblack min-w-[100px] shrink-0">
                              Timeframe:
                            </AdminTableBody>
                            <AdminMainText className="text-offblack">{getUrgencyLabel(quote.urgency)}</AdminMainText>
                          </div>
                        </div>
                        
                        {/* Photos */}
                        {parsedPhotos && parsedPhotos.length > 0 && (
                          <div className="mt-3">
                            <div className="grid grid-cols-2 gap-2">
                              {parsedPhotos.slice(0, 4).map((photo, idx) => (
                                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-gray-200">
                                  <img
                                    src={photo.url || photo}
                                    alt={`Photo ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Timestamp */}
                      <div className="mt-1 px-2 text-left">
                        <AdminMetadataText className="text-mediumgray">{item.timestamp}</AdminMetadataText>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            
            // Skip rendering if message text is empty
            if (!item.text || !item.text.trim()) {
              return null;
            }
            
            return (
            <div
              key={item.id}
              className={`flex ${item.sender === "business" ? "justify-end" : "justify-start"}`}
            >
              <div className={businessChatMessageRowClass(item.sender)}>
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className={BUSINESS_CHAT_AVATAR_CLASS}
                  />
                </div>
                
                {/* Message Bubble */}
                <div className="flex flex-col">
                  <div className={businessChatBubbleClass(item.sender)}>
                    {item.type === "text" && (
                      <AdminMainText
                        className={`${
                          item.sender === "business" ? "text-white" : "text-offblack"
                        } leading-relaxed`}
                      >
                        {item.text}
                      </AdminMainText>
                    )}
                    
                    {item.type === "image" && (
                      <div className="space-y-2">
                        <img
                          src={item.imageUrl}
                          alt="Shared image"
                          className="max-w-full h-auto rounded-lg"
                          style={{ maxWidth: "200px" }}
                        />
                        {item.text && (
                          <AdminMainText
                            className={`${
                              item.sender === "business" ? "text-white" : "text-offblack"
                            } leading-relaxed`}
                          >
                            {item.text}
                          </AdminMainText>
                        )}
                      </div>
                    )}
                    
                    {item.type === "file" && (
                      <div className="flex items-center gap-3 p-2 bg-black bg-opacity-10 rounded-lg">
                        <div className="flex-shrink-0">
                          <i className="fi fi-rs-file text-2xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <AdminMainText
                            className={`${
                              item.sender === "business" ? "text-white" : "text-offblack"
                            } truncate`}
                          >
                            {item.fileName}
                          </AdminMainText>
                          <AdminMetadataText
                            className={
                              item.sender === "business" ? "text-white/80" : "text-mediumgray"
                            }
                          >
                            {item.fileSize}
                          </AdminMetadataText>
                        </div>
                        <button className="flex-shrink-0 p-1 hover:bg-black hover:bg-opacity-20 rounded">
                          <i className="fi fi-rs-download text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Timestamp */}
                  <div className={`mt-1 px-2 ${item.sender === "business" ? "text-right" : "text-left"}`}>
                    <AdminMetadataText className="text-mediumgray">{item.timestamp}</AdminMetadataText>
                  </div>
          </div>
              </div>
          </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Typing Input Area */}
        <div className="bg-white p-4 px-20 w-full mx-auto">
          {chatBlocked ? (
            <div className="py-3 px-4 rounded-lg bg-mistygray text-center">
              <AdminSupportingText className="text-mediumgray">
                This chat is blocked. No one can send messages in this chat room.
              </AdminSupportingText>
            </div>
          ) : (
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleComposerKeyDown}
                placeholder="Type a message..."
                rows={1}
                className={`${adminWizardInputClasses(false)} resize-none rounded-full !max-w-full w-full pr-12 !h-auto !py-[13px] !leading-4`}
                style={{
                  minHeight: "44px",
                  maxHeight: "120px",
                }}
              />
              {/* 3-dot Menu button - floated inside input on the right, circle style */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                <button
                  type="button"
                  className="pointer-events-auto w-8 h-8 rounded-full border border-gray-300 hover:border-gray-400 flex items-center justify-center text-offblack transition-colors"
                  onClick={() => setShowMenu(!showMenu)}
                  aria-label="Options"
                >
                  <i className="fi fi-bs-menu-dots text-sm flex items-center justify-center"></i>
                </button>
              </div>
              {showMenu && (
                <div 
                  ref={dotMenuRef}
                  className="flex flex-col rounded-xl w-40 py-2 absolute z-30 bg-white text-offblack bottom-full right-0 mb-2 shadow-lg border border-gray-200"
                >
                  <button
                    type="button"
                    className="block px-4 py-2 hover:text-primaryblue w-full text-left flex items-center gap-2"
                    onClick={() => {
                      setShowMenu(false);
                      fileInputRef.current?.click();
                    }}
                  >
                    <i className="fi fi-rs-clip text-sm" aria-hidden />
                    <AdminMainText>Attachment</AdminMainText>
                  </button>
                  <button
                    type="button"
                    className="block px-4 py-2 hover:text-primaryblue w-full text-left flex items-center gap-2"
                    onClick={() => {
                      setShowMenu(false);
                      setReportOpen(true);
                      setReportStep(1);
                      setReportReason("");
                      setReportBlock(null);
                    }}
                  >
                    <i className="fi fi-rs-flag text-sm" aria-hidden />
                    <AdminMainText>Report</AdminMainText>
                  </button>
                  <button
                    type="button"
                    className="block px-4 py-2 hover:text-primaryblue w-full text-left flex items-center gap-2"
                    onClick={() => setShowMenu(false)}
                  >
                    <i className="fi fi-rs-trash text-sm" aria-hidden />
                    <AdminMainText>Delete</AdminMainText>
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept={`${getAcceptAttribute("image")},${getAcceptAttribute("document")}`}
              />
            </div>
            <AdminHeaderAction
              type="button"
              className="shrink-0 !min-w-0 !px-0 !w-[2.125rem] flex items-center justify-center mb-[5px]"
              onClick={handleSendMessage}
              disabled={!message.trim()}
              aria-label="Send message"
            >
              <i className="fi fi-rs-paper-plane text-base flex items-center justify-center w-full h-full" aria-hidden />
            </AdminHeaderAction>
          </div>
          )}
        </div>
      </div>

      {/* Report popup - same UI as existing Popup */}
      {reportOpen && (
        <Popup
          setTrigger={() => {
            setReportOpen(false);
            setReportStep(1);
            setReportReason("");
            setReportBlock(null);
          }}
          onClose={() => {
            setReportOpen(false);
            setReportStep(1);
            setReportReason("");
            setReportBlock(null);
          }}
          ShowCrossButton={true}
          cross={true}
          businessDashboard={true}
          hideLogoInHeader={true}
        >
          <div className="overflow-auto relative flex items-center justify-center hidden-scrollbar" style={{ minHeight: "calc(100vh - 120px)" }}>
            <div className={`${POPUP_FORM_CONTENT_WIDTH_CLASS} mx-auto pt-10 pb-20`}>
              {reportStep === 1 && (
                <>
                  <AdminPageTitle className="text-center pb-0 mt-10 xl:mt-8 lg:mt-20">
                    Report this chat
                  </AdminPageTitle>
                  <div className="mt-8">
                    <AdminDetailFieldLabel className="mb-2">Reason for report</AdminDetailFieldLabel>
                    <AdminSupportingText className="text-mediumgray mb-4 block">
                      Select a reason to report this chat.
                    </AdminSupportingText>
                    <div className="space-y-2 mb-6">
                      {REPORT_REASONS.map((reason) => (
                        <button
                          key={reason}
                          type="button"
                          onClick={() => setReportReason(reason)}
                          className={reportOptionClass(reportReason === reason)}
                        >
                          <AdminMainText>{reason}</AdminMainText>
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-3 justify-end mt-10">
                      <AdminFormSecondaryAction type="button" onClick={() => setReportOpen(false)}>
                        Cancel
                      </AdminFormSecondaryAction>
                      <AdminHeaderAction type="button" onClick={() => setReportStep(2)} disabled={!reportReason}>
                        Next
                      </AdminHeaderAction>
                    </div>
                  </div>
                </>
              )}
              {reportStep === 2 && (
                <>
                  <AdminPageTitle className="text-center pb-0 mt-10 xl:mt-8 lg:mt-20">
                    Block this chat?
                  </AdminPageTitle>
                  <div className="mt-8">
                    <AdminSupportingText className="text-mediumgray mb-6 block">
                      If you block this chat, no one will be able to message in this chat room.
                    </AdminSupportingText>
                    <div className="flex gap-3 mb-6">
                      <button
                        type="button"
                        onClick={() => setReportBlock(true)}
                        className={`flex-1 ${reportOptionClass(reportBlock === true)}`}
                      >
                        <AdminMainText>Yes, block chat</AdminMainText>
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportBlock(false)}
                        className={`flex-1 ${reportOptionClass(reportBlock === false)}`}
                      >
                        <AdminMainText>No</AdminMainText>
                      </button>
                    </div>
                    <div className="flex gap-3 justify-end mt-10">
                      <AdminFormSecondaryAction type="button" onClick={() => setReportStep(1)}>
                        Back
                      </AdminFormSecondaryAction>
                      <AdminHeaderAction type="button" onClick={() => setReportStep(3)} disabled={reportBlock === null}>
                        Next
                      </AdminHeaderAction>
                    </div>
                  </div>
                </>
              )}
              {reportStep === 3 && (
                <>
                  <AdminPageTitle className="text-center pb-0 mt-10 xl:mt-8 lg:mt-20">
                    Confirm report
                  </AdminPageTitle>
                  <div className="mt-8">
                    <div className="mb-6 space-y-2">
                      <AdminMainText className="text-offblack">
                        <AdminTableBody emphasis as="span">
                          Reason:
                        </AdminTableBody>{" "}
                        {reportReason}
                      </AdminMainText>
                      <AdminMainText className="text-offblack">
                        <AdminTableBody emphasis as="span">
                          Block chat:
                        </AdminTableBody>{" "}
                        {reportBlock ? "Yes" : "No"}
                      </AdminMainText>
                    </div>
                    <div className="flex gap-3 justify-end mt-10">
                      <AdminFormSecondaryAction type="button" onClick={() => setReportStep(2)}>
                        Back
                      </AdminFormSecondaryAction>
                      <AdminHeaderAction
                        type="button"
                        onClick={() => {
                          if (reportBlock) {
                            setChatBlocked(true);
                            Alerts.success("Chat blocked. No one can message in this chat room.");
                          } else {
                            Alerts.success("Report submitted. A case has been opened in moderation.");
                          }
                          setReportOpen(false);
                          setReportStep(1);
                          setReportReason("");
                          setReportBlock(null);
                        }}
                      >
                        Submit report
                      </AdminHeaderAction>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};

export default MessageLayout;
